import {z} from 'zod'

import {FormValidationInput} from '../../../types'
import {ErrorCodes} from '../../common/validation/errorCodes'
import {validateFieldRange} from '../../common/validation/validateFieldRange'
import {QuantityConfiguration, QuantitySelection} from '../../quantity/types'
import {computeLoadBalanceRange, isLoadBalanceEligible} from '../loadBalance.utils'
import {LoadBalanceConfiguration, LoadBalanceForm} from '../types'

type ValidationData = LoadBalanceForm & {
  quantity: QuantitySelection
}

type ValidationConfiguration = LoadBalanceConfiguration & QuantityConfiguration

export const validateLoadBalance = (
  data: ValidationData,
  ctx: z.RefinementCtx,
  {configuration}: FormValidationInput<ValidationConfiguration>
) => {
  const config = configuration.loadBalance
  if (!config.isEnabled) {
    if (data.maxLoadSize !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['maxLoadSize'],
        message: ErrorCodes.maxLoadSize.notAllowed
      })
    }
    return
  }

  // Skip load balance validation for fullLoads mode
  if (data.quantity.type === 'fullLoads') {
    return
  }

  const truckSize = config.maxTruckSize
  const step = configuration.quantity.step
  const uom = configuration.quantity.uom
  const volume = data.quantity.volume

  const range = isLoadBalanceEligible(config, volume)
    ? computeLoadBalanceRange({quantity: volume, truckSize, quantityStep: step})
    : undefined

  if (!range) {
    // Quantity outside balance load range — maxLoadSize must not be set
    if (data.maxLoadSize !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['maxLoadSize'],
        message: ErrorCodes.maxLoadSize.notAllowed
      })
    }
    return
  }

  // In range — value is required
  if (data.maxLoadSize === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['maxLoadSize'],
      message: ErrorCodes.maxLoadSize.required
    })
    return
  }

  validateFieldRange(
    data.maxLoadSize,
    {min: range.min, max: range.max, step, uom},
    'maxLoadSize',
    'maxLoadSize',
    ctx
  )
}
